import { useEffect, useState } from 'react'
import type { PaycomRow } from '../types'
import { fetchPaycomRaw } from '../lib/api'
import { ExportButtons } from '../components/ExportButtons'
import { money, prettyDate } from '../lib/format'
import { useBrandFilter } from '../lib/brand'

const STATUS_STYLES: Record<string, string> = {
  succeeded: 'bg-emerald-500/20 text-emerald-300',
  refunded:  'bg-amber-500/20 text-amber-300',
  failed:    'bg-red-500/20 text-red-300',
}

export function PayCom() {
  const [brand] = useBrandFilter()
  const [rows, setRows] = useState<PaycomRow[] | null>(null)
  const [limit, setLimit] = useState<number>(1000)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  async function load(n = limit) {
    setLoading(true)
    setError(null)
    try {
      setRows(await fetchPaycomRaw(n))
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brand])

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Pay.com</h1>
          <p className="text-sm text-slate-400">
            Raw charges ingested from the Pay.com webhook (`recon_paycom_transactions`).
          </p>
        </div>
        <div className="flex items-center gap-3">
          <label className="text-sm text-slate-400">
            Rows:{' '}
            <select
              value={limit}
              onChange={(e) => {
                const v = Number(e.target.value)
                setLimit(v)
                load(v)
              }}
              className="bg-slate-800 border border-slate-700 rounded px-2 py-1 text-slate-200"
            >
              <option value={500}>500</option>
              <option value={1000}>1 000</option>
              <option value={5000}>5 000</option>
              <option value={20000}>20 000</option>
            </select>
          </label>
          <ExportButtons name="paycom" rows={rows as unknown as Record<string, unknown>[] | null} />
        </div>
      </div>

      {error && (
        <div className="border border-red-500/40 bg-red-500/10 text-red-200 rounded px-3 py-2 text-sm">
          {error}
        </div>
      )}

      {loading && !rows && <div className="text-slate-400 text-sm">Loading…</div>}

      {rows && (
        <div className="border border-slate-800 rounded-lg overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-900 text-slate-400 text-xs uppercase tracking-wide">
              <tr className="whitespace-nowrap">
                {brand === 'ALL' && <th className="text-left px-3 py-2">Brand</th>}
                <th className="text-left px-3 py-2">Reference</th>
                <th className="text-left px-3 py-2">Charge ID</th>
                <th className="text-right px-3 py-2">Amount</th>
                <th className="text-left px-3 py-2">Currency</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-left px-3 py-2">Customer</th>
                <th className="text-left px-3 py-2 whitespace-nowrap">Captured (UTC)</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={`${r.brand}-${r.reference}`} className="border-t border-slate-800/80">
                  {brand === 'ALL' && (
                    <td className="px-3 py-2">
                      <span className={`inline-block px-2 py-0.5 rounded text-xs ${r.brand === 'ROLLA' ? 'bg-red-500/20 text-red-300' : 'bg-indigo-500/20 text-indigo-300'}`}>
                        {r.brand}
                      </span>
                    </td>
                  )}
                  <td className="px-3 py-2 font-mono">{r.reference}</td>
                  <td className="px-3 py-2 font-mono text-xs text-slate-400">
                    {r.charge_id}
                  </td>
                  <td className="px-3 py-2 text-right font-mono">
                    {money(r.amount)}
                  </td>
                  <td className="px-3 py-2 uppercase">{r.currency}</td>
                  <td className="px-3 py-2">
                    <span
                      className={`inline-block px-2 py-0.5 rounded text-xs ${
                        STATUS_STYLES[r.status] ?? 'bg-slate-500/20 text-slate-300'
                      }`}
                    >
                      {r.status}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-slate-400">
                    {r.customer_email ?? '—'}
                  </td>
                  <td className="px-3 py-2 text-slate-400 whitespace-nowrap">
                    {prettyDate(r.captured_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
